import { CONFIG } from '../../config';
import { Boss } from '../Boss';
import { Bullet } from '../Bullet';

/**
 * 보스 C - 분열자
 * 피격 시 2개로 분할, 최대 2단계 분할 (총 4개)
 * 작아질수록 빠르고 공격적
 */
export class Splitter extends Boss {
    private fireTimer: number = 0;
    private fireInterval: number;
    private moveSpeed: number;
    private moveAngle: number;
    private splitLevel: number; // 0: 원본, 1: 1차 분할, 2: 2차 분할
    private hasEntered: boolean = false;
    private isLandscape: boolean;

    constructor(
        canvasWidth: number,
        canvasHeight: number,
        x?: number,
        y?: number,
        splitLevel: number = 0,
        isLandscape: boolean = false
    ) {
        // 분할 레벨에 따른 체력 설정
        const baseHealth = CONFIG.BOSS.SPLITTER.HEALTH;
        const health = Math.ceil(baseHealth / Math.pow(2, splitLevel));

        // 시작 위치 (분할 시에는 부모 위치 사용, 아니면 화면 밖)
        let startX: number;
        let startY: number;
        if (x !== undefined && y !== undefined) {
            // 분열 시 부모 위치 사용
            startX = x;
            startY = y;
        } else {
            // 화면 밖에서 시작 (세로: 상단, 가로: 우측)
            startX = isLandscape ? canvasWidth + 60 : canvasWidth / 2;
            startY = isLandscape ? canvasHeight / 2 : -60;
        }

        super(startX, startY, health, 'splitter', canvasWidth, canvasHeight);

        this.splitLevel = splitLevel;
        this.isLandscape = isLandscape;
        // 분열된 보스는 이미 입장한 것으로 처리
        this.hasEntered = x !== undefined && y !== undefined;

        // 분할 레벨에 따른 크기 조정
        this.radius = CONFIG.BOSS.SPLITTER.BASE_RADIUS / Math.pow(1.5, splitLevel);

        // 분할될수록 약간 빠름 (증가량 감소: 0.5 → 0.25)
        this.moveSpeed = CONFIG.BOSS.SPLITTER.MOVE_SPEED * (1 + splitLevel * 0.25);
        // 분할 후 발사 간격 증가 (더 느리게)
        this.fireInterval = CONFIG.BOSS.SPLITTER.FIRE_INTERVAL * (1 + splitLevel * 0.3);

        // 랜덤 이동 방향 (입장 중에는 화면 중앙 방향)
        if (this.hasEntered) {
            this.moveAngle = Math.random() * Math.PI * 2;
        } else {
            this.moveAngle = Math.atan2(canvasHeight / 2 - startY, canvasWidth / 2 - startX);
        }
    }

    update(deltaTime: number, playerX: number, playerY: number): void {
        if (!this.isAlive) return;

        // 입장 체크 (화면 안에 들어왔는지)
        if (!this.hasEntered) {
            const margin = this.radius + 20;
            if (this.x > margin && this.x < this.canvasWidth - margin &&
                this.y > margin && this.y < this.canvasHeight - margin) {
                this.hasEntered = true;
                // 입장 후 랜덤 방향으로 변경
                this.moveAngle = Math.random() * Math.PI * 2;
            }
        }

        // 이동
        this.x += Math.cos(this.moveAngle) * this.moveSpeed * deltaTime;
        this.y += Math.sin(this.moveAngle) * this.moveSpeed * deltaTime;

        // 입장 후에만 벽 반사
        if (this.hasEntered) {
            if (this.x <= this.radius || this.x >= this.canvasWidth - this.radius) {
                this.moveAngle = Math.PI - this.moveAngle;
                this.x = Math.max(this.radius, Math.min(this.canvasWidth - this.radius, this.x));
            }
            if (this.y <= this.radius || this.y >= this.canvasHeight - this.radius) {
                this.moveAngle = -this.moveAngle;
                this.y = Math.max(this.radius, Math.min(this.canvasHeight - this.radius, this.y));
            }

            // 플레이어 방향으로 약간 유도
            const dx = playerX - this.x;
            const dy = playerY - this.y;
            const targetAngle = Math.atan2(dy, dx);
            const angleDiff = targetAngle - this.moveAngle;

            // 각도 정규화
            let normalizedDiff = angleDiff;
            while (normalizedDiff > Math.PI) normalizedDiff -= Math.PI * 2;
            while (normalizedDiff < -Math.PI) normalizedDiff += Math.PI * 2;

            this.moveAngle += normalizedDiff * 0.02; // 천천히 방향 전환
        }

        // 회전 애니메이션
        this.rotation += 2 * deltaTime * (1 + this.splitLevel);

        // 입장 후에만 총알 발사
        if (this.hasEntered) {
            this.fireTimer += deltaTime;
            if (this.fireTimer >= this.fireInterval) {
                this.fireTimer = 0;
                this.fireBullets(playerX, playerY);
            }
        }
    }

    private fireBullets(playerX: number, playerY: number): void {
        const bulletSpeed = 80 + this.splitLevel * 15; // 속도 증가량 감소 (20 → 15)
        // 0단계: 2발, 1단계: 2발, 2단계: 1발 (기존 3,3,1 → 2,2,1)
        const bulletCount = this.splitLevel >= 2 ? 1 : 2;

        // 플레이어 방향으로 발사
        const dx = playerX - this.x;
        const dy = playerY - this.y;
        const baseAngle = Math.atan2(dy, dx);

        for (let i = 0; i < bulletCount; i++) {
            const angle = baseAngle + (i - (bulletCount - 1) / 2) * (Math.PI / 6); // 30도 간격
            const vx = Math.cos(angle) * bulletSpeed;
            const vy = Math.sin(angle) * bulletSpeed;

            const bullet = new Bullet(this.x, this.y, vx, vy, '#FFFFFF');
            bullet.hasEnteredPlayArea = true;
            bullet.noBounce = true; // 반사 없이 화면 밖으로 사라짐
            this.bulletBuffer.push(bullet);
        }
    }

    /**
     * 분열 처리
     * @returns 분열된 새 Splitter 배열 또는 null (최대 분할에 도달)
     */
    split(): Splitter[] | null {
        if (this.splitLevel >= CONFIG.BOSS.SPLITTER.MAX_SPLIT_LEVEL) {
            return null;
        }

        const newLevel = this.splitLevel + 1;

        // 분열 방향: 현재 이동 방향의 수직 방향으로 튀어나감
        const perpAngle = this.moveAngle + Math.PI / 2;

        // 2개의 새 Splitter 생성 (같은 위치에서 시작, 반대 방향으로 이동)
        const splitter1 = new Splitter(
            this.canvasWidth,
            this.canvasHeight,
            this.x,
            this.y,
            newLevel,
            this.isLandscape
        );
        splitter1.moveAngle = perpAngle;

        const splitter2 = new Splitter(
            this.canvasWidth,
            this.canvasHeight,
            this.x,
            this.y,
            newLevel,
            this.isLandscape
        );
        splitter2.moveAngle = perpAngle + Math.PI;

        return [splitter1, splitter2];
    }

    /**
     * 분할 레벨 반환
     */
    getSplitLevel(): number {
        return this.splitLevel;
    }

    /**
     * 분할 가능 여부
     */
    canSplit(): boolean {
        return this.splitLevel < CONFIG.BOSS.SPLITTER.MAX_SPLIT_LEVEL;
    }

    private getColor(): string {
        // 분할 레벨에 따른 색상
        const colors = ['#FF66FF', '#FF99FF', '#FFCCFF'];
        return colors[this.splitLevel] || '#FFFFFF';
    }

    render(ctx: CanvasRenderingContext2D): void {
        if (!this.isAlive) return;

        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);

        // 외곽 (다각형 - 하얀색 라인만, 항상 6각형 유지)
        const sides = 6;
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2;

        ctx.beginPath();
        for (let i = 0; i < sides; i++) {
            const angle = (i / sides) * Math.PI * 2;
            const px = Math.cos(angle) * this.radius;
            const py = Math.sin(angle) * this.radius;
            if (i === 0) {
                ctx.moveTo(px, py);
            } else {
                ctx.lineTo(px, py);
            }
        }
        ctx.closePath();
        ctx.stroke();

        // 체력 표시 (내부 원 - 라인만)
        const healthRatio = this.health / this.maxHealth;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(0, 0, this.radius * 0.6 * healthRatio, 0, Math.PI * 2);
        ctx.stroke();

        // 분할 레벨 표시 (작은 원들 - 라인만)
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 1;
        for (let i = 0; i <= this.splitLevel; i++) {
            ctx.beginPath();
            ctx.arc((i - this.splitLevel / 2) * 8, 0, 3, 0, Math.PI * 2);
            ctx.stroke();
        }

        ctx.restore();
    }

    getName(): string {
        const levelNames = ['SPLITTER', 'SPLITTER II', 'SPLITTER III'];
        return levelNames[this.splitLevel] || 'SPLITTER';
    }
}
